import type { StashItem } from './types.js';
import {
  $, stashBar, stashItems, exportBtn, copyBtn,
  statusBar, mainSvg, mainCanvas, mainPanel, toast,
} from './dom.js';

/** Sample the RGBA pixel at (x, y) from a flat pixel buffer and return a hex color string. */
export function sampleColor(rgba: Uint8ClampedArray, imgW: number, x: number, y: number): string {
  const i = (y * imgW + x) * 4;
  const r = rgba[i], g = rgba[i + 1], b = rgba[i + 2];
  return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
}

/** Build a complete SVG string from the stash items, using each item's source color and path data. */
export function buildStashSvg(stash: StashItem[], imgW: number, imgH: number): string {
  let paths = '';
  // Reverse so the newest stash item (index 0, via unshift) paints last (on top in SVG).
  for (let i = stash.length - 1; i >= 0; i--) {
    const item = stash[i];
    if (item.include === false) continue;
    const color = item.sourceColor;
    if (item.groupPaths) {
      const subPaths = item.groupPaths
        .map(p => `<path d="${p.path}" fill="${color}" stroke="none" fill-rule="evenodd"/>`)
        .join('\n    ');
      if (item.name) {
        paths += `  <g id="${item.name}">\n    ${subPaths}\n  </g>\n`;
      } else {
        paths += `  ${subPaths}\n`;
      }
    } else {
      const path = `<path d="${item.path}" fill="${color}" stroke="none" fill-rule="evenodd"/>`;
      if (item.name) {
        paths += `  <g id="${item.name}">\n    ${path}\n  </g>\n`;
      } else {
        paths += `  ${path}\n`;
      }
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${imgW} ${imgH}" width="${imgW}" height="${imgH}">\n${paths}</svg>`;
}

/** Render the stash panel UI: populates the stash bar with interactive items (color picker, rename, toggle export/visibility, remove). */
export function renderStash(
  stash: StashItem[],
  updatePreview: () => void,
  saveState: () => void,
  saveFileStash: () => void,
): void {
  stashBar.style.display = stash.length > 0 ? 'flex' : 'none';
  stashItems.innerHTML = '';
  stash.forEach((item, idx) => {
    const el = document.createElement('div');
    el.className = 'stash-item' + (item.visible ? '' : ' hidden-item');
    el.innerHTML =
      `<input type="color" value="${item.color}" title="Change color">
       <span class="stash-name" style="display:flex;align-items:center;gap:4px">
         <span style="width:8px;height:8px;border-radius:50%;background:${item.sourceColor || item.color};flex-shrink:0"></span>
         <span style="overflow:hidden;text-overflow:ellipsis">${item.name || item.id}${item.groupPaths ? ' (' + item.groupPaths.length + ')' : ''}</span>
       </span>
       <span style="flex:1"></span>
       <div style="display:flex;align-items:center;gap:6px;margin-left:8px">
         <button title="Toggle export" style="font-size:10px;line-height:1;font-weight:bold;opacity:${item.include !== false ? 1 : 0.3}">E</button>
         <button title="Toggle visibility" style="font-size:11px;line-height:1">${item.visible ? '\u25C9' : '\u25CB'}</button>
         <button title="Remove" style="font-size:11px;line-height:1;color:#ef4444;font-weight:bold">&#x2715;</button>
       </div>`;

    const nameSpan = el.querySelector('.stash-name') as HTMLElement;
    const onDblClick = (e: Event): void => {
      e.stopPropagation();
      const span = e.currentTarget as HTMLElement;
      const current = item.name || item.id;
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'stash-name-input';
      input.value = current;
      span.replaceWith(input);
      input.focus();
      input.select();
      const confirm = (): void => {
        if (!input.parentNode) return;
        const val = input.value.trim();
        if (val && val !== item.id) item.name = val;
        else item.name = '';
        const newSpan = document.createElement('span');
        newSpan.className = 'stash-name';
        newSpan.textContent = item.name || item.id;
        newSpan.addEventListener('dblclick', onDblClick);
        input.replaceWith(newSpan);
        saveState();
        saveFileStash();
      };
      input.addEventListener('blur', confirm);
      input.addEventListener('keydown', (ev: KeyboardEvent) => {
        if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
        if (ev.key === 'Escape') { input.value = current; input.blur(); }
      });
    };
    nameSpan.addEventListener('dblclick', onDblClick);

    (el.querySelector('input[type=color]') as HTMLInputElement).oninput = (e: Event) => {
      item.color = (e.target as HTMLInputElement).value;
      updatePreview();
      saveState();
    };
    const buttons = el.querySelectorAll('button');
    buttons[0].onclick = () => {
      item.include = item.include === false ? undefined : false;
      exportBtn.disabled = copyBtn.disabled = !stash.some(i => i.include !== false);
      renderStash(stash, updatePreview, saveState, saveFileStash);
      saveState();
      saveFileStash();
    };
    buttons[1].onclick = () => {
      item.visible = !item.visible;
      renderStash(stash, updatePreview, saveState, saveFileStash);
      updatePreview();
      saveState();
    };
    buttons[2].onclick = () => {
      stash.splice(idx, 1);
      exportBtn.disabled = copyBtn.disabled = !stash.some(i => i.include !== false);
      renderStash(stash, updatePreview, saveState, saveFileStash);
      updatePreview();
      saveState();
      saveFileStash();
    };
    stashItems.appendChild(el);
  });
}

/** Show a brief toast notification that fades out after 1.5 seconds. */
export function showToast(msg: string): void {
  toast.textContent = msg;
  toast.style.opacity = '1';
  setTimeout(() => { toast.style.opacity = '0'; }, 1500);
}
